import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { ITraining, BaseVideoFormData } from '../../types';
import { apiService } from '../../api/api.service';

interface InstructionState {
    videos: ITraining[];
    selectedVideo: ITraining | null;
    isLoading: boolean;
    error: string | null;
    deleteDialog: {
        open: boolean;
        videoId: number | null;
    };
    openDialog: boolean;
}

const initialState: InstructionState = {
    videos: [],
    selectedVideo: null,
    isLoading: false,
    error: null,
    deleteDialog: {
        open: false,
        videoId: null
    },
    openDialog: false
};

// Async thunks
export const fetchInstructionVideos = createAsyncThunk(
    'instruction/fetch',
    async (language: string, { rejectWithValue }) => {
        try {
            const data = await apiService.getVideoList('instruction');
            return {
                data,
                language
            };
        } catch (error) {
            return rejectWithValue('Failed to fetch instruction videos');
        }
    }
);

export const uploadInstructionVideo = createAsyncThunk(
    'instruction/upload',
    async ({
        training,
        language,
        videoFile,
        thumbnailFile
    }: {
        training: BaseVideoFormData['training'],
        language: string,
        videoFile?: File,
        thumbnailFile?: File
    }, { rejectWithValue }) => {
        try {
            let video_file_path = undefined;
            let preview_file_path = undefined;

            // Загружаем видео файл, если он есть
            if (videoFile) {
                const videoResponse = await apiService.uploadInstructionVideoFile(videoFile);
                video_file_path = videoResponse.file_path;
            }

            // Загружаем превью файл, если он есть
            if (thumbnailFile) {
                const previewResponse = await apiService.uploadInstructionPreviewFile(thumbnailFile);
                preview_file_path = previewResponse.file_path;
            }

            const videoData = {
                ...training,
                language: language.toUpperCase(),
                title: training.title || `instruction_${language.toLowerCase()}`,
                video_file_path,
                preview_file_path
            };

            const response = await apiService.uploadVideo('instruction', videoData);
            return response;
        } catch (error) {
            return rejectWithValue('Failed to upload instruction video');
        }
    }
);

export const editInstructionVideo = createAsyncThunk(
    'instruction/edit',
    async ({
        id,
        training,
        language,
        videoFile,
        thumbnailFile
    }: {
        id: number;
        training: BaseVideoFormData['training'],
        language: string,
        videoFile?: File,
        thumbnailFile?: File
    }, { rejectWithValue }) => {
        try {
            let video_file_path = undefined;
            let preview_file_path = undefined;

            // Загружаем видео файл, если он есть
            if (videoFile) {
                const videoResponse = await apiService.uploadInstructionVideoFile(videoFile);
                video_file_path = videoResponse.file_path;
            }

            // Загружаем превью файл, если он есть
            if (thumbnailFile) {
                const previewResponse = await apiService.uploadInstructionPreviewFile(thumbnailFile);
                preview_file_path = previewResponse.file_path;
            }

            const videoData = {
                ...training,
                language: language.toUpperCase(),
                title: training.title || `instruction_${language.toLowerCase()}`,
                video_file_path,
                preview_file_path
            };

            const response = await apiService.editVideo('instruction', id, videoData);
            return response;
        } catch (error) {
            return rejectWithValue('Failed to edit instruction video');
        }
    }
);

export const deleteInstructionVideo = createAsyncThunk(
    'instruction/delete',
    async (id: number, { rejectWithValue }) => {
        try {
            await apiService.deleteVideo('instruction', id);
            return id;
        } catch (error) {
            return rejectWithValue('Failed to delete instruction video');
        }
    }
);

const instructionSlice = createSlice({
    name: 'instruction',
    initialState,
    reducers: {
        setSelectedVideo: (state, action: PayloadAction<ITraining | null>) => {
            state.selectedVideo = action.payload;
        },
        setOpenDialog: (state, action: PayloadAction<boolean>) => {
            state.openDialog = action.payload;
        },
        setDeleteDialog: (state, action: PayloadAction<{ open: boolean; videoId: number | null }>) => {
            state.deleteDialog = action.payload;
        },
        updateVideoFromStatus: (state, action: PayloadAction<{
            videoId: number;
            updates: Partial<ITraining>
        }>) => {
            const { videoId, updates } = action.payload;

            const videoIndex = state.videos.findIndex(v => v.id === videoId);
            if (videoIndex !== -1) {
                state.videos[videoIndex] = {
                    ...state.videos[videoIndex],
                    ...updates
                };
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchInstructionVideos
            .addCase(fetchInstructionVideos.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchInstructionVideos.fulfilled, (state, action) => {
                const { data, language } = action.payload;
                state.videos = data.filter(
                    video => video.language && video.language.toUpperCase() === language.toUpperCase()
                );
                state.isLoading = false;
            })
            .addCase(fetchInstructionVideos.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            // uploadInstructionVideo
            .addCase(uploadInstructionVideo.fulfilled, (state, action) => {
                state.videos = [action.payload];
                state.openDialog = false;
            })

            // editInstructionVideo
            .addCase(editInstructionVideo.fulfilled, (state, action) => {
                state.videos = [action.payload];
                state.openDialog = false;
            })

            // deleteInstructionVideo
            .addCase(deleteInstructionVideo.fulfilled, (state, action) => {
                state.videos = [];
                state.deleteDialog = {
                    open: false,
                    videoId: null
                };
            });
    }
});

export const {
    setSelectedVideo,
    setOpenDialog,
    setDeleteDialog,
    updateVideoFromStatus
} = instructionSlice.actions;

export default instructionSlice.reducer; 